import React, { useEffect } from "react";
import TopNav from "../../components/TopNav.jsx";
import SiteFooter from "../../components/SiteFooter.jsx";

/** Renders text with given phrases wrapped in <strong>. All occurrences are bolded; longest phrases first to avoid partial matches. */
function withBold(text, phrases) {
  if (!phrases || phrases.length === 0) return text;
  const sorted = [...phrases].filter(Boolean).sort((a, b) => b.length - a.length);
  const matches = [];
  for (const phrase of sorted) {
    let pos = 0;
    while (true) {
      const idx = text.indexOf(phrase, pos);
      if (idx === -1) break;
      matches.push({ start: idx, end: idx + phrase.length, phrase });
      pos = idx + phrase.length;
    }
  }
  matches.sort((a, b) => a.start - b.start);
  let last = 0;
  const out = [];
  for (let i = 0; i < matches.length; i++) {
    const m = matches[i];
    if (m.start < last) continue;
    if (m.start > last) out.push(text.slice(last, m.start));
    out.push(<strong key={i} className="font-semibold text-slate-800">{m.phrase}</strong>);
    last = m.end;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const SECTIONS = [
  {
    title: "Customer Privacy Notice",
    subs: [
      {
        heading: "WHY SHOULD YOU READ THIS DOCUMENT?",
        body: `During the course of dealing with us, we will ask you to provide us with detailed personal information relating to your existing circumstances, your financial situation and, in some cases, your health and family health history ("Your Personal Data").
This document is important as it allows us to explain to you what we will need to do with Your Personal Data, and the various rights you have in relation to Your Personal Data.
Dynesourcer Ltd acts as an Introducer Appointed Representative (IAR) of Dynamic Commercial Finance Ltd, which is authorised and regulated by the Financial Conduct Authority.`,
        boldPhrases: ["Dynesourcer Ltd", "Dynamic Commercial Finance Ltd", "Financial Conduct Authority"],
      },
      {
        heading: "WHAT DO WE MEAN BY \"YOUR PERSONAL DATA\"?",
        body: `Your Personal Data means any information that describes or relates to your personal circumstances.
Your Personal Data may identify you directly, for example your name, address, date of birth, National Insurance number.
Your Personal Data may also identify you indirectly, for example, your employment situation, your physical and mental health history, or any other information that could be associated with your cultural or social identity.

In the context of providing you with assistance in relation to your Finance requirements, Your Personal Data may include:
• Title, names, date of birth, gender, nationality, civil/marital status, contact details, addresses and documents that are necessary to verify your identity
• Employment and remuneration information (including salary/bonus schemes/overtime/sick pay/other benefits), employment history
• Bank account details, tax information, loans and credit commitments, personal credit history, sources of income and expenditure, family circumstances and details of dependents
• Health status and history, details of treatment and prognosis, medical reports (further details are provided below specifically with regard to the processing we may undertake in relation to this type of information)
• Any pre-existing finance products and the terms and conditions relating to these`,
      },
      {
        heading: "SPECIAL CATEGORIES OF PERSONAL DATA",
        body: `Certain data is classified under the Regulation as "special categories":
Racial or ethnic origin, political opinions, religious beliefs, trade union membership, genetic data, biometric data, health data, data concerning a natural person's sex life or sexual orientation.
Consent is required for Dynesourcer Ltd to process both types of personal data, but it must be explicitly given.
Where we are asking you for sensitive personal data, we will always tell you why and how the information will be used.`,
        boldPhrases: ["Dynesourcer Ltd"],
      },
      {
        heading: "THE BASIS UPON WHICH OUR FIRM WILL DEAL WITH YOUR PERSONAL DATA",
        body: `When we speak with you about your finance requirements, we do so on the basis that both parties are entering a contract for the supply of services.
In order to perform that contract, and to arrange the products you require, we have the right to use Your Personal Data for the purposes detailed below.
Alternatively, either in the course of initial discussions with you or when the contract between us has come to an end for whatever reason, we have the right to use Your Personal Data provided it is in our legitimate business interest to do so and your rights are not affected.
For example, we may need to respond to requests from lenders and our Compliance Service Provider (Dynamic Commercial Finance Ltd) relating to the advice we have given to you, or to make contact with you to seek feedback on the service you received.
On occasion, we will use Your Personal Data for contractual responsibilities we may owe our regulator, the Financial Conduct Authority, or for wider compliance with any legal or regulatory obligation to which we might be subject.
In such circumstances, we would be processing Your Personal Data in order to meet a legal, compliance or other regulatory obligation to which we are subject.`,
        boldPhrases: ["Dynamic Commercial Finance Ltd", "Financial Conduct Authority"],
      },
      {
        heading: "THE BASIS UPON WHICH WE WILL PROCESS CERTAIN PARTS OF YOUR PERSONAL DATA",
        body: `In asking us to assist you with your finance needs, we may need to ask you for information in relation to Special Category Data.
We will record and use Special Category Data in order to assist with enquiries of lenders in relation to their products and to provide you with advice or guidance regarding the suitability of any product that may be available to you.
We will use Special Category Data and any criminal disclosures in the same way as Your Personal Data generally, as set out in this Privacy Notice.
Information on Special Category Data and criminal disclosures must be capable of being exchanged freely between broker intermediaries such as Dynesourcer Ltd, its Principal Firm Dynamic Commercial Finance Ltd, and lenders to enable customers to secure the lending that their needs require.`,
        boldPhrases: ["Dynesourcer Ltd", "Dynamic Commercial Finance Ltd"],
      },
      {
        heading: "HOW DO WE COLLECT YOUR PERSONAL DATA?",
        body: `We will collect and record Your Personal Data from a variety of sources, but mainly directly from you.
You will usually provide information during the course of our initial meetings or conversations with you to establish your circumstances, needs and preferences in relation to finance.
You may provide information verbally, in writing, or by email.
We may also obtain information from third parties, for example credit reference agencies, lenders, your employer, and searches of publicly available information such as the electoral roll.
Where technology solutions are used to assist in the collection of Your Personal Data (for example software used to verify credit status), this will only be done where appropriate consent has been obtained or where permitted by law.
Electronic ID checks do not require your consent, but we will inform you of how such systems operate and the purpose for which they are used.`,
      },
      {
        heading: "WHAT HAPPENS TO YOUR PERSONAL DATA WHEN IT IS DISCLOSED TO US?",
        body: `In the course of handling Your Personal Data, we will:
• Record and store Your Personal Data in paper files, mobile devices and computer systems including email, hard drives and cloud-based systems
• Restrict access to Your Personal Data to authorised employees and consultants only, and only where necessary to provide services to you
• Use Your Personal Data to respond to queries, progress finance applications and inform you of relevant developments`,
      },
      {
        heading: "SHARING YOUR PERSONAL DATA",
        body: `From time to time, Your Personal Data will be shared with:
• Finance lenders
• Dynamic Commercial Finance Ltd, as our Principal Firm & Appointed Representatives (AR's).
• Third parties who assist with your enquiry or application, including compliance advisers, product specialists, solicitors, estate planners, conveyancers, surveyors and valuers
In all cases, Your Personal Data will only be shared for the purposes set out in this Customer Privacy Notice.
This sharing does not entitle third parties to send you marketing or promotional communications.`,
        boldPhrases: ["Dynamic Commercial Finance Ltd"],
      },
      {
        heading: "INTERNATIONAL TRANSFERS",
        body: `We do not envisage that the performance of our services will involve Your Personal Data being transferred outside the European Economic Area.`,
      },
      {
        heading: "SECURITY AND RETENTION OF YOUR PERSONAL DATA",
        body: `Your privacy is important to us and we will keep Your Personal Data secure in accordance with our legal responsibilities.
We will take reasonable steps to safeguard Your Personal Data against unlawful or unauthorised access.
We also expect you to take reasonable steps to protect your own privacy when transferring information to us.
Your Personal Data will be retained for a minimum of six years, or longer where required by law or regulatory obligations.`,
      },
      {
        heading: "YOUR RIGHTS IN RELATION TO YOUR PERSONAL DATA",
        body: `You can:
• Request copies of Your Personal Data held by us
• Ask for further information about how we use Your Personal Data
• Ask us to correct, delete or restrict the use of Your Personal Data
• Request an electronic copy of Your Personal Data
• Withdraw consent previously given for marketing or other purposes`,
      },
      {
        heading: "HOW TO CONTACT US",
        body: `If you have any questions about this Privacy Notice or wish to exercise your rights, please contact:
Dynesourcer Ltd
Data Protection Enquiries
c/o Dynamic Commercial Finance Ltd
Ground Floor, 3 Brooklands Court
Kettering Venture Park
Kettering, Northamptonshire
NN15 6FD
United Kingdom
admin@dynesourcer.co.uk
As Dynesourcer Ltd is an Introducer Appointed Representative, you may also contact our Principal Firm:
Dynamic Commercial Finance Ltd
Ground Floor, 3 Brooklands Court
Kettering Venture Park
Kettering, Northamptonshire
NN15 6FD
United Kingdom
If we have a legal reason not to comply with a request, or to act differently than requested, we will explain this to you.
admin@dynamiccf.co.uk
If you become aware of any unauthorised disclosure of Your Personal Data, please contact us immediately.`,
        boldPhrases: [
          "Dynesourcer Ltd",
          "Data Protection Enquiries",
          "admin@dynesourcer.co.uk",
          "Dynamic Commercial Finance Ltd",
          "c/o Dynamic Commercial Finance Ltd",
          "Ground Floor, 3 Brooklands Court",
          "Kettering Venture Park",
          "Kettering, Northamptonshire",
          "NN15 6FD",
          "United Kingdom",
          "admin@dynamiccf.co.uk",
        ],
      },
      {
        heading: "COMPLAINTS",
        body: `If you have concerns about how your Personal Data has been handled, you may complain to the UK data protection regulator:
Information Commissioner's Office (ICO)
Wycliffe House
Water Lane
Wilmslow
Cheshire
SK9 5AF
Website: https://ico.org.uk`,
        boldPhrases: [
          "Information Commissioner's Office (ICO)",
          "Wycliffe House",
          "Water Lane",
          "Wilmslow",
          "Cheshire",
          "SK9 5AF",
          "Website: https://ico.org.uk",
        ],
      },
    ],
  },
];

function PrivacyContent() {
  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12 md:py-16">
      {SECTIONS.map((section, i) => (
        <div key={i}>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-8">
            {section.title}
          </h1>
          <div className="space-y-10">
            {section.subs.map((sub, j) => (
              <section key={j} className="space-y-3">
                <h2 className="text-lg font-bold text-slate-800">
                  {sub.heading}
                </h2>
                <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                  {sub.boldPhrases ? withBold(sub.body, sub.boldPhrases) : sub.body}
                </div>
              </section>
            ))}
          </div>
        </div>
      ))}
    </article>
  );
}

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-white text-slate-800 pt-24">
      <TopNav />
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-2xl md:text-3xl font-black text-slate-900">
            Privacy Policy &amp; Customer Privacy Notice
          </h1>
          <p className="mt-2 text-slate-600">
            How we collect, use and protect your personal data.
          </p>
        </div>
      </section>
      <PrivacyContent />
      <SiteFooter />
    </main>
  );
}
